
import { useState, useEffect } from "react";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { LoadingButton } from "@/components/ui/loading-button";
import { PRO_FEATURES, PREMIUM_FEATURES, PRO_TIER, PREMIUM_TIER, TEAMS_TIER, TEAMS_FEATURES } from "@/components/plans/types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { useTranslations } from "@/hooks/useTranslations";
import { useMetaPixel } from "@/hooks/useMetaPixel";
import { useAnalytics } from "@/hooks/useAnalytics";

interface PricingProps {
  hideFreePlan?: boolean;
  expiredTrial?: boolean;
}

export const Pricing = ({
  hideFreePlan = false,
  expiredTrial = false
}: PricingProps) => {
  const { user, profile } = useAuth();
  const { t, language } = useLanguage();
  const { getPath } = useLanguagePath();
  const { t: dashboardT } = useTranslations("dashboard");
  const { 
    sendInitiateCheckoutEvent, 
    sendStartTrialEvent, 
    sendViewContentEvent,
    sendSubscriptionUpgradeEvent 
  } = useMetaPixel();
  
  const {
    sendViewItemEvent,
    sendSelectItemEvent,
    sendBeginCheckoutEvent
  } = useAnalytics();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [showTrial, setShowTrial] = useState<boolean>(!expiredTrial);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);

  const currentPlan = profile?.subscription_plan || 'free';
  const isTrialPlan = currentPlan === 'trial';

  const hasHadTrialBefore = expiredTrial || 
    (profile?.subscription_plan && profile.subscription_plan !== 'free') || 
    (profile?.subscription_end_date !== undefined && profile.subscription_end_date !== null);

  // Enviar eventos de visualização quando a seção de pricing é visualizada
  useEffect(() => {
    sendViewContentEvent('Pricing Plans', 'pricing_page', 0);
    
    // Enhanced Ecommerce: View Item events para cada plano
    sendViewItemEvent('pro_plan', 'Professional Plan', 'subscription', PRO_TIER.price);
    sendViewItemEvent('premium_plan', 'Premium Plan', 'subscription', PREMIUM_TIER.price);
    sendViewItemEvent('teams_plan', 'Teams Plan', 'subscription', TEAMS_TIER.price);
  }, [sendViewContentEvent, sendViewItemEvent]);

  const mapFeatureKey = (key: string) => {
    // Special cases for certain features with different mappings
    if (key === "subscription.planFeatures.chat") {
      return "landing.pricing.planFeatures.chatAndChannels";
    }
    else if (key === "subscription.planFeatures.createProductsPayments") {
      return "landing.pricing.planFeatures.createProductsPayments";
    }
    
    // Standard mapping for other features
    return key.replace('subscription.planFeatures', 'landing.pricing.planFeatures');
  };

  const handleSubscription = async (priceId: string, planType?: string, planValue?: number, planId?: string) => {
    try {
      setIsLoading(true);
      setErrorDetails(null);
      
      if (!user) {
        window.location.href = getPath('/auth');
        return;
      }

      // Analytics: Select Item event
      if (planId) {
        sendSelectItemEvent(planId, planType || 'Unknown Plan', 'subscription');
      }

      // Analytics: Begin Checkout event
      if (planValue && planId && planType) {
        sendBeginCheckoutEvent(planId, planType, planValue);
      }

      // Enviar evento InitiateCheckout para planos pagos (Meta Pixel)
      if (priceId !== 'trial' && planType && planValue) {
        sendInitiateCheckoutEvent(planType, planValue);
      }

      // Enviar evento StartTrial para trials (Meta Pixel)
      if (priceId === 'trial') {
        sendStartTrialEvent('Premium');
      }

      // Enviar evento de upgrade se é um upgrade de trial (Meta Pixel)
      if (isTrialPlan && planType && planValue) {
        sendSubscriptionUpgradeEvent('Trial', planType, planValue);
      }
      
      const {
        data,
        error
      } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId,
          userId: user.id,
          returnUrl: getPath('/dashboard'),
          currentPath: window.location.pathname
        }
      });
      
      if (error) {
        console.error('Invoke error:', error);
        throw new Error(error.message || 'Erro ao criar sessão de checkout');
      }
      
      if (!data) {
        console.error('No data received from function');
        throw new Error('Nenhum dado recebido do servidor');
      }
      
      if (data.error) {
        console.error('Function returned error:', data.error);
        setErrorDetails(data.error);
        throw new Error(data.error);
      }
      
      // For trial subscriptions
      if (priceId === 'trial' && data.success) {
        toast.success(language === 'pt' 
          ? "Seu período de teste foi ativado com sucesso!" 
          : "Your trial period has been successfully activated!");
        
        // Redirect to dashboard or refresh page to show updated subscription
        window.location.href = data.redirectUrl;
        return;
      }
      
      // For regular subscriptions
      if (data?.url) {
        console.log("Redirecionando para Stripe:", data.url);
        window.location.href = data.url;
      } else {
        toast.error(language === 'pt'
          ? 'Erro ao redirecionar para checkout'
          : 'Error redirecting to checkout');
        console.error('No URL in response:', data);
      }
      
    } catch (error: any) {
      console.error('Error details:', error);
      const errorMessage = errorDetails || error.message || 'Erro ao processar assinatura';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const openStripePortal = async () => {
    try {
      setIsLoadingPortal(true);

      // Get current path including language prefix
      const currentPath = window.location.pathname;

      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: {
          currentPath: currentPath  // Pass current path to edge function
        }
      });

      if (error || !data?.url) {
        toast.error(language === 'pt'
          ? "Erro inesperado ao abrir o portal do Stripe."
          : "Unexpected error opening Stripe portal.");
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(language === 'pt'
        ? "Erro inesperado ao abrir o portal do Stripe."
        : "Unexpected error opening Stripe portal.");
    } finally {
      setIsLoadingPortal(false);
    }
  };

  return (
    <div id="pricing" className="py-8 md:py-24 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {!hideFreePlan && (
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {t('landing.pricing.title')}
            </h2>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Professional Plan */}
          <div className="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-xl md:text-2xl font-bold mb-2 dark:text-white">
              {t('landing.pricing.professional')}
            </h3>
            
            <div className="mb-6">
              <span className="text-4xl md:text-5xl font-bold dark:text-white">
                €{PRO_TIER.price.toFixed(2)}
              </span>
              <span className="text-gray-600 dark:text-gray-400">{t('landing.pricing.perMonth')}</span>
            </div>
            
            <ul className="space-y-3 mb-6">
              {PRO_FEATURES.map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-sm md:text-base">
                  <ShieldCheck className="h-4 w-4 md:h-5 md:w-5 text-blue-600 dark:text-blue-400 shrink-0" />
                  <span className="text-gray-600 dark:text-gray-300">{t(mapFeatureKey(feature.text))}</span>
                </li>
              ))}
            </ul>
            
            <LoadingButton 
              loading={isLoading || isLoadingPortal} 
              onClick={() => {
                if (user && (profile?.subscription_plan === 'pro' || profile?.subscription_plan === 'premium')) {
                  openStripePortal();
                } else {
                  handleSubscription(PRO_TIER.priceId, 'Professional', PRO_TIER.price, 'pro_plan');
                }
              }} 
              variant="outline"
              className="w-full py-3 md:py-6 text-base md:text-lg dark:border-gray-600 dark:hover:bg-gray-700"
            >
              {isTrialPlan 
                ? dashboardT('subscription.buttons.upgrade')
                : user && (profile?.subscription_plan === 'pro' || profile?.subscription_plan === 'premium')
                  ? language === 'pt' ? "Gerenciar" : "Manage"
                  : user ? t('landing.pricing.features.buyPlan') : t('landing.pricing.features.startNow')}
            </LoadingButton>
          </div>

          {/* Premium Plan */}
          <div className="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-lg border-2 border-blue-600 dark:border-blue-500 relative">
            {/* Recommended Badge */}
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-flame text-white px-4 py-1 rounded-full text-sm font-medium">
                {t('landing.pricing.recommended')}
              </span>
            </div>

            <h3 className="text-xl md:text-2xl font-bold mb-2 dark:text-white">
              {showTrial && !expiredTrial ? t('landing.pricing.trial') : t('landing.pricing.premium')}
            </h3>
            
            <div className="mb-4">
              {showTrial && !expiredTrial ? (
                <div className="flex items-baseline">
                  <span className="text-4xl md:text-5xl font-bold dark:text-white line-through text-gray-500">
                    €19.90
                  </span>
                  <span className="text-gray-600 dark:text-gray-400 ml-2">{t('landing.pricing.perMonth')}</span>
                </div>
              ) : (
                <div>
                  <span className="text-4xl md:text-5xl font-bold dark:text-white">
                    €{PREMIUM_TIER.price.toFixed(2)}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">{t('landing.pricing.perMonth')}</span>
                </div>
              )}
              {showTrial && !expiredTrial && (
                <>
                  <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">{t('landing.pricing.freeDays')}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t('landing.pricing.afterTrial')} €{PREMIUM_TIER.price.toFixed(2)} {t('landing.pricing.perMonth')}
                  </p>
                </>
              )}
            </div>

            {!expiredTrial && (
              <div className="flex justify-center mb-4">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-full p-1">
                  <button 
                    onClick={() => setShowTrial(true)}
                    className={`px-4 py-2 rounded-full text-sm ${showTrial ? 'bg-white dark:bg-gray-700 shadow-sm' : ''}`}
                  >
                    {t('landing.pricing.trial')}
                  </button>
                  <button 
                    onClick={() => setShowTrial(false)}
                    className={`px-4 py-2 rounded-full text-sm ${!showTrial ? 'bg-white dark:bg-gray-700 shadow-sm' : ''}`}
                  >
                    {t('landing.pricing.paidPlan')}
                  </button>
                </div>
              </div>
            )}
            
            <ul className="space-y-3 mb-6">
              {PREMIUM_FEATURES.map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-sm md:text-base">
                  <ShieldCheck className="h-4 w-4 md:h-5 md:w-5 text-blue-600 dark:text-blue-400 shrink-0" />
                  <span className="text-gray-600 dark:text-gray-300">
                    {t(mapFeatureKey(feature.text))}
                    {feature.comingSoon && (
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({language === 'pt' ? 'Em Breve' : 'Coming Soon'})
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            
            <LoadingButton 
              loading={isLoading || isLoadingPortal} 
              onClick={() => {
                if (user && (profile?.subscription_plan === 'pro' || profile?.subscription_plan === 'premium')) {
                  openStripePortal();
                } else {
                  const planType = showTrial && !expiredTrial ? 'Trial' : 'Premium';
                  const value = showTrial && !expiredTrial ? 0 : PREMIUM_TIER.price;
                  const planId = showTrial && !expiredTrial ? 'premium_trial' : 'premium_plan';
                  handleSubscription(
                    showTrial && !expiredTrial ? 'trial' : PREMIUM_TIER.priceId,
                    planType,
                    value,
                    planId
                  );
                }
              }} 
              className="w-full py-3 md:py-6 text-base md:text-lg bg-blue-600 hover:bg-blue-700"
              disabled={(showTrial && hasHadTrialBefore)}
            >
              {showTrial && !expiredTrial
                ? hasHadTrialBefore ? t('landing.pricing.features.usedTrial') : t('landing.pricing.features.startTrial')
                : isTrialPlan 
                  ? dashboardT('subscription.buttons.upgrade')
                  : user && (profile?.subscription_plan === 'pro' || profile?.subscription_plan === 'premium')
                    ? language === 'pt' ? "Gerenciar" : "Manage"
                    : user ? t('landing.pricing.features.buyPlan') : t('landing.pricing.features.startNow')}
            </LoadingButton>
            
            {showTrial && !expiredTrial && !hasHadTrialBefore && (
              <p className="text-sm text-center mt-2 text-gray-500 dark:text-gray-400">
                {t('landing.pricing.features.noCard')}
              </p>
            )}
          </div>

          {/* Teams Plan */}
          <div className="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-xl md:text-2xl font-bold mb-2 dark:text-white">
              {t('landing.pricing.teams')}
            </h3>
            
            {/* Removing price display for Teams plan */}
            <div className="mb-6">
              <span className="text-lg font-medium mt-2 text-blue-600 dark:text-blue-400">
                {language === 'pt' ? 'Em Breve' : 'Coming Soon'}
              </span>
            </div>
            
            <ul className="space-y-3 mb-6">
              {TEAMS_FEATURES.map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-sm md:text-base">
                  <ShieldCheck className="h-4 w-4 md:h-5 md:w-5 text-blue-600 dark:text-blue-400 shrink-0" />
                  <span className="text-gray-600 dark:text-gray-300">{t(mapFeatureKey(feature.text))}</span>
                </li>
              ))}
            </ul>
            
            <LoadingButton 
              variant="outline" 
              className="w-full py-3 md:py-6 text-base md:text-lg dark:border-gray-600"
              disabled
            >
              {language === 'pt' ? 'Em Breve' : 'Coming Soon'}
            </LoadingButton>
          </div>
        </div>

        {errorDetails && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            <p className="font-medium mb-1">{language === 'pt' ? 'Erro técnico' : 'Technical error'}</p>
            <code className="block whitespace-pre-wrap">{errorDetails}</code>
          </div>
        )}
      </div>
    </div>
  );
};
